import client from '@/apollo-client';
import { ArrowDown } from '@/assets/icons/info-icons/arrowDown';
import { DocumentIcon } from '@/assets/static/icons/document';
import { ArrowUp } from '@/assets/static/icons/todo-page/arrow-up';
import { themeVar } from '@/styles/theme';
import { useState } from 'react';
import styled from 'styled-components';
import { EllipsisWithTooltip } from '../basicComponents/ellipsisWithTooltip';
import { GET_ASSET_SIGNED_URL_QUERY } from '../settings-page/queries';
import { GET_AI_ASSET_KEY_QUERY } from './queries';
import { IAiRunPromptAnswerCitation } from './types';

interface CitationProps {
  ids: {
    recordId: string;
    investmentId: string;
    funnelId?: string;
  };
  fieldName: string;
  citations: IAiRunPromptAnswerCitation[];
  defaultShowCitations?: boolean;
  scrollToBottom?: (scrollBehavior: ScrollBehavior) => void;
}

export const Citation = ({ ids, fieldName, citations, defaultShowCitations, scrollToBottom }: CitationProps) => {
  const [showCitation, setShowCitation] = useState(defaultShowCitations ?? true);

  const showSuggestions = () => {
    setShowCitation(!showCitation);
    if (scrollToBottom) {
      scrollToBottom('smooth');
    }
  };

  const openPdf = async (pageNumber: string, filename: string) => {
    try {
      const key = await client.query({
        fetchPolicy: 'network-only',
        query: GET_AI_ASSET_KEY_QUERY,
        variables: {
          data: {
            recordId: ids.recordId,
            filename: filename
          }
        }
      });
      const { data } = await client.query({
        fetchPolicy: 'network-only',
        query: GET_ASSET_SIGNED_URL_QUERY,
        variables: {
          assetKey: key.data.getDealAssistentAIAssetKey
        }
      });
      window.open(`${data.getAssetSignedUrl}#page=${pageNumber}`, '_blank');
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <AllSuggestionsWrapper>
      <SuggestionsHeader showCitation={showCitation} onClick={showSuggestions}>
        {fieldName}
        {showCitation ? (
          <ArrowUp width={23} height={23} fill={themeVar('font.action')} />
        ) : (
          <ArrowDown style={{ position: 'relative', top: '1px', right: '4px' }} width={12} height={12} fill={themeVar('font.action')} />
        )}
      </SuggestionsHeader>
      <SuggestionsBody showCitation={showCitation}>
        <ExpandableWrapp>
          {citations.map((citation, index) => (
            <FilesWrapper key={index}>
              {citation?.fileReferences?.length > 0 ? (
                <>
                  {citation?.fileReferences?.map((file: any, index: number) => (
                    <div key={index}>
                      <FileName>
                        <DocumentIconWrapper>
                          <DocumentIcon fill={themeVar('font.base')} />
                        </DocumentIconWrapper>
                        <EllipsisWithTooltip title={file.filename} forceRecalculate={showCitation}>
                          {file.filename}
                        </EllipsisWithTooltip>
                      </FileName>
                      <PageReference>
                        Page Reference
                        {file?.pageNumbers?.map((page: any, index: number) => (
                          <Page key={index} onClick={() => openPdf(page, file.filename)}>{`${page}${
                            index < file?.pageNumbers?.length - 1 ? ', ' : ''
                          }`}</Page>
                        ))}
                      </PageReference>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {citation?.filename && citation?.pageNumbers?.length > 0 && (
                    <>
                      <FileName>
                        <DocumentIconWrapper>
                          <DocumentIcon fill={themeVar('font.base')} />
                        </DocumentIconWrapper>
                        <EllipsisWithTooltip title={citation?.filename} forceRecalculate={showCitation}>
                          {citation?.filename}
                        </EllipsisWithTooltip>
                      </FileName>
                      <PageReference>
                        Page Reference
                        {citation?.pageNumbers?.map((page: any, index: number) => (
                          <Page key={index} onClick={() => openPdf(page, citation?.filename)}>{`${page}${
                            index < citation?.pageNumbers?.length - 1 ? ', ' : ''
                          }`}</Page>
                        ))}
                      </PageReference>
                    </>
                  )}
                </>
              )}
              <AiAnswerWrapper>
                <div>
                  <b>AI Answer</b>
                </div>
                {citation?.answer}
              </AiAnswerWrapper>
            </FilesWrapper>
          ))}
        </ExpandableWrapp>
      </SuggestionsBody>
    </AllSuggestionsWrapper>
  );
};

const AllSuggestionsWrapper = styled.div`
  padding: 20px;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const SuggestionsHeader = styled.div<{ showCitation: boolean }>`
  font-weight: 700;
  line-height: 20px;
  color: ${themeVar('font.action')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border-bottom: ${({ showCitation }) => (showCitation ? `1px solid ${themeVar('border.base')}` : 'none')};
  padding-bottom: 10px;
`;

const ExpandableWrapp = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SuggestionsBody = styled.div<{ showCitation: boolean }>`
  display: grid;
  grid-template-rows: ${({ showCitation }) => (showCitation ? '1fr' : '0fr')};
  overflow: ${({ showCitation }) => (showCitation ? 'visible' : 'hidden')};
  transition: grid-template-rows 300ms;
`;

const FilesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
`;

const DocumentIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PageReference = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 5px 30px;
`;

const Page = styled.span`
  cursor: pointer;
  color: ${themeVar('font.action')};
`;

const AiAnswerWrapper = styled.div``;
